import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../common/Toast';

const PLANS = [
  {
    key: 'gratuit',
    name: 'Gratuit',
    price: '0',
    period: '/ mois',
    description: 'Idéal pour découvrir la plateforme et accéder aux ressources en libre accès.',
    features: [
      { label: 'Création de compte immédiate', included: true },
      { label: 'Accès aux résumés de toutes les publications', included: true },
      { label: 'Accès complet aux publications "Gratuit"', included: true },
      { label: 'Accès aux publications "Classique"', included: false },
      { label: 'Accès aux publications "Premium"', included: false },
      { label: 'Téléchargement des rapports complets', included: false },
    ],
  },
  {
    key: 'classique',
    name: 'Classique',
    price: '15 000',
    period: 'FCFA / mois',
    description: 'Pour les chercheurs, étudiants et professionnels ayant besoin d\'un accès régulier.',
    featured: true,
    badge: 'Le plus choisi',
    amount: 15000,
    features: [
      { label: 'Tous les avantages de la formule Gratuit', included: true },
      { label: 'Accès complet aux publications "Gratuit" et "Classique"', included: true },
      { label: 'Téléchargement des documents autorisés', included: true },
      { label: 'Alertes sur les nouvelles publications', included: true },
      { label: 'Accès aux publications "Premium"', included: false },
    ],
  },
  {
    key: 'premium',
    name: 'Premium',
    price: '35 000',
    period: 'FCFA / mois',
    description: 'Accès intégral pour les institutions, entreprises et centres de recherche.',
    amount: 35000,
    features: [
      { label: 'Tous les avantages de la formule Classique', included: true },
      { label: 'Accès complet à 100% des publications, sans restriction', included: true },
      { label: 'Téléchargement illimité', included: true },
      { label: 'Support prioritaire & accès anticipé aux nouveautés', included: true },
      { label: 'Rapports sur-mesure (sur demande)', included: true },
    ],
  },
];

const Subscriptions = () => {
  const { user, updatePlan, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [welcome, setWelcome] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  // Vérifier le paramètre welcome dans l'URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('welcome') === '1') {
      setWelcome(true);
      // Supprimer le paramètre de l'URL sans recharger la page
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // Vérifier si une transaction est en cours
  useEffect(() => {
    const checkPendingTransaction = async () => {
      const pendingPayment = sessionStorage.getItem('aeii_pending_payment');
      if (pendingPayment) {
        try {
          const transaction = JSON.parse(pendingPayment);
          console.log('📦 Transaction en attente:', transaction);
          
          // Vérifier si la transaction est récente (moins de 10 minutes)
          const timestamp = transaction.timestamp || 0;
          const age = Date.now() - timestamp;
          
          if (age > 600000) { // 10 minutes
            // Transaction trop ancienne, on la nettoie
            sessionStorage.removeItem('aeii_pending_payment');
            console.log('🧹 Transaction trop ancienne, nettoyée');
          } else if (user && transaction.plan === user.plan) {
            // La transaction est déjà terminée
            sessionStorage.removeItem('aeii_pending_payment');
            console.log('✅ Transaction déjà complétée, nettoyée');
          }
        } catch (error) {
          console.error('Erreur vérification transaction:', error);
          sessionStorage.removeItem('aeii_pending_payment');
        }
      }
    };

    checkPendingTransaction();
  }, [user]);

  // Fonction principale de sélection de plan
  const handleSelectPlan = async (planKey) => {
    // Réinitialiser les erreurs
    setPaymentError(null);

    // === PLAN GRATUIT ===
    if (planKey === 'gratuit') {
      if (!user) {
        sessionStorage.setItem('aeii_redirect_after_login', '/abonnements');
        navigate('/connexion');
        return;
      }
      
      if (user.plan === 'gratuit') {
        showToast('Vous êtes déjà sur la formule Gratuit');
        return;
      }

      try {
        setLoading(planKey);
        await updatePlan('gratuit');
        showToast('✅ Vous êtes maintenant sur la formule Gratuit');
        await refreshUser();
      } catch (err) {
        showToast('❌ Erreur lors du changement de formule', 'error');
        setPaymentError(err.message);
      } finally {
        setLoading(null);
      }
      return;
    }

    // === PLANS PAYANTS (Classique et Premium) ===
    
    // Vérifier si l'utilisateur est connecté
    if (!user) {
      sessionStorage.setItem('aeii_redirect_after_login', '/abonnements');
      navigate('/connexion');
      return;
    }

    // Vérifier si l'utilisateur a déjà ce plan
    if (user.plan === planKey) {
      showToast(`ℹ️ Vous avez déjà l'abonnement ${planKey}`);
      return;
    }

    // Vérifier si une transaction est déjà en cours
    const pendingPayment = sessionStorage.getItem('aeii_pending_payment');
    if (pendingPayment) {
      try {
        const transaction = JSON.parse(pendingPayment);
        const age = Date.now() - (transaction.timestamp || 0);
        if (age < 600000) { // Moins de 10 minutes
          showToast('⏳ Une transaction est déjà en cours. Veuillez patienter.', 'error');
          return;
        } else {
          sessionStorage.removeItem('aeii_pending_payment');
        }
      } catch (e) {
        sessionStorage.removeItem('aeii_pending_payment');
      }
    }

    // 🔴 LANCER LE PROCESSUS DE PAIEMENT
    try {
      setLoading(planKey);
      
      // Déterminer le montant
      const amount = planKey === 'classique' ? 15000 : 35000;
      
      // Sauvegarder les informations de la transaction dans la session
      sessionStorage.setItem('aeii_pending_payment', JSON.stringify({
        plan: planKey,
        amount: amount,
        timestamp: Date.now(),
        userId: user.id,
        userEmail: user.email
      }));

      console.log(`🔄 Lancement du paiement pour ${planKey} - ${amount} FCFA`);
      
      // Rediriger vers le formulaire de paiement
      navigate('/payment-form');

    } catch (error) {
      console.error('❌ Erreur:', error);
      setPaymentError(error.message || 'Erreur lors de la préparation du paiement');
      showToast('❌ ' + (error.message || 'Erreur lors de la préparation du paiement'), 'error');
      setLoading(null);
      
      // Nettoyer la session en cas d'erreur
      sessionStorage.removeItem('aeii_pending_payment');
    }
  };

  // Vérifier si un plan est actif
  const isPlanActive = (planKey) => {
    if (!user) return false;
    if (planKey === 'gratuit') {
      // Le plan gratuit est toujours actif
      return true;
    }
    return user.plan === planKey;
  };

  // Vérifier si un plan est en attente de paiement
  const isPlanPending = (planKey) => {
    const pending = sessionStorage.getItem('aeii_pending_payment');
    if (!pending) return false;
    try {
      const transaction = JSON.parse(pending);
      return transaction.plan === planKey;
    } catch (e) {
      return false;
    }
  };

  // Récupérer la transaction en attente
  const getPendingTransaction = () => {
    const pending = sessionStorage.getItem('aeii_pending_payment');
    if (!pending) return null;
    try {
      const transaction = JSON.parse(pending);
      const age = Date.now() - (transaction.timestamp || 0);
      if (age < 600000) { // Moins de 10 minutes
        return transaction;
      } else {
        sessionStorage.removeItem('aeii_pending_payment');
        return null;
      }
    } catch (e) {
      return null;
    }
  };

  const pendingTransaction = getPendingTransaction();

  return (
    <>
      <section className="page-header">
        <div className="container text-center">
          <h1>Nos formules d'abonnement</h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', maxWidth: '600px', margin: '0 auto' }}>
            Choisissez la formule adaptée à vos besoins de recherche et d'accès à notre bibliothèque documentaire.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          {/* Message de bienvenue */}
          {welcome && (
            <div className="form-msg success" style={{ display: 'block', marginBottom: '24px', textAlign: 'center' }}>
              <i className="fa-solid fa-check-circle"></i> Bienvenue sur AEII ! Votre compte gratuit est actif. 
              Choisissez une formule ci-dessous pour débloquer davantage de contenus.
            </div>
          )}

          {/* Message de transaction en cours */}
          {pendingTransaction && (
            <div className="form-msg" style={{ 
              display: 'block', 
              marginBottom: '24px', 
              textAlign: 'center',
              background: '#ebf8ff',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #bee3f8'
            }}>
              <i className="fa-solid fa-spinner fa-spin"></i> 
              Vous avez une transaction en cours pour la formule <strong>{pendingTransaction.plan}</strong>.
              <br />
              <span style={{ fontSize: '0.85rem', color: '#4a5568' }}>
                {pendingTransaction.amount.toLocaleString()} FCFA
              </span>
              <div style={{ marginTop: '12px' }}>
                <Link to="/payment-form" className="btn btn-primary btn-sm">
                  <i className="fa-solid fa-arrow-right"></i> Continuer le paiement
                </Link>
              </div>
            </div>
          )}

          {/* Message d'erreur de paiement */}
          {paymentError && (
            <div className="form-msg error" style={{ display: 'block', marginBottom: '24px' }}>
              <i className="fa-solid fa-exclamation-circle"></i> {paymentError}
            </div>
          )}

          <div className="pricing-grid">
            {PLANS.map((plan) => {
              const isCurrent = user && user.plan === plan.key;
              const isPopular = plan.featured;
              const isPaidPlan = plan.key !== 'gratuit';
              const isDisabled = loading === plan.key;
              const isActive = isPlanActive(plan.key);
              const isPending = pendingTransaction && pendingTransaction.plan === plan.key;

              return (
                <div 
                  key={plan.key} 
                  className={`price-card ${isPopular ? 'featured' : ''} ${isActive ? 'active' : ''}`}
                >
                  {/* Badges */}
                  {plan.badge && <span className="price-badge">{plan.badge}</span>}
                  
                  {isActive && !isPaidPlan && (
                    <span className="price-badge" style={{ background: '#48bb78', color: '#fff' }}>
                      <i className="fa-solid fa-check"></i> Actif
                    </span>
                  )}
                  
                  {isActive && isPaidPlan && (
                    <span className="price-badge" style={{ background: '#48bb78', color: '#fff' }}>
                      <i className="fa-solid fa-check"></i> Actif
                    </span>
                  )}
                  
                  {isPending && (
                    <span className="price-badge" style={{ background: '#ed8936', color: '#fff' }}>
                      <i className="fa-solid fa-clock"></i> En cours
                    </span>
                  )}
                  
                  {/* Contenu */}
                  <span className="price-name">{plan.name}</span>
                  <div className="price-amount">
                    {plan.price} <span>{plan.period}</span>
                  </div>
                  <p style={{ color: '#667180', fontSize: '0.92rem' }}>{plan.description}</p>

                  <ul className="price-features">
                    {plan.features.map((f, idx) => (
                      <li key={idx} className={f.included ? '' : 'disabled'}>
                        <i className={`fa-solid ${f.included ? 'fa-check' : 'fa-xmark'}`} />
                        {f.label}
                      </li>
                    ))}
                  </ul>

                  {/* Informations de paiement */}
                  {isPaidPlan && !isActive && (
                    <div style={{ 
                      fontSize: '0.85rem', 
                      color: '#667180', 
                      textAlign: 'center',
                      background: '#f8f9fa',
                      padding: '8px',
                      borderRadius: '8px',
                      marginTop: '-10px'
                    }}>
                      <i className="fa-solid fa-credit-card"></i> Paiement sécurisé via Monetbil
                    </div>
                  )}

                  {isActive && isPaidPlan && (
                    <div style={{ 
                      fontSize: '0.85rem', 
                      color: '#48bb78', 
                      textAlign: 'center',
                      background: '#f0fff4',
                      padding: '8px',
                      borderRadius: '8px',
                      marginTop: '-10px',
                      border: '1px solid #c6f6d5'
                    }}>
                      <i className="fa-solid fa-check-circle"></i> Abonnement actif
                      {user?.plan_expires_at && (
                        <span style={{ display: 'block', fontSize: '0.75rem', color: '#666', marginTop: '4px' }}>
                          Valable jusqu'au {new Date(user.plan_expires_at).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Bouton d'action */}
                  <button
                    className={`btn ${isActive ? 'btn-ghost' : isPopular ? 'btn-primary' : 'btn-dark'} btn-block`}
                    onClick={() => handleSelectPlan(plan.key)}
                    disabled={isDisabled || isActive || isPending}
                  >
                    {isDisabled ? (
                      <><i className="fa-solid fa-spinner fa-spin"></i> Chargement...</>
                    ) : isPending ? (
                      <><i className="fa-solid fa-clock"></i> En cours...</>
                    ) : isActive ? (
                      'Formule actuelle'
                    ) : isPaidPlan ? (
                      `Souscrire à ${plan.name} - ${plan.amount.toLocaleString()} FCFA`
                    ) : user ? (
                      'Passer à Gratuit'
                    ) : (
                      `Choisir ${plan.name}`
                    )}
                  </button>

                  {isPaidPlan && !isActive && (
                    <div style={{ fontSize: '0.75rem', color: '#999', textAlign: 'center', marginTop: '-10px' }}>
                      <i className="fa-solid fa-mobile-screen-button"></i> Paiement via Mobile Money (Orange Money, MTN, etc.)
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="text-center" style={{ marginTop: '40px', color: '#667180', fontSize: '0.9rem' }}>
            <p>
              <i className="fa-solid fa-lock" style={{ marginRight: '8px' }}></i>
              Paiement sécurisé via Monetbil. Aucune information bancaire n'est stockée sur notre plateforme.
            </p>
            <p style={{ marginTop: '8px', fontSize: '0.85rem' }}>
              <i className="fa-solid fa-info-circle"></i> En cliquant sur "Souscrire", vous serez redirigé vers le formulaire de paiement.
            </p>
          </div>
        </div>
      </section>

      {/* Section FAQ */}
      <section className="section bg-alt">
        <div className="container">
          <span className="section-kicker">Questions fréquentes</span>
          <h2 className="section-title">Comment fonctionne l'accès aux documents ?</h2>
          <div className="grid grid-2" style={{ marginTop: '24px' }}>
            <div className="value-card">
              <i className="fa-solid fa-lock"></i>
              <h4>Accès conditionné à l'abonnement</h4>
              <p>
                Chaque publication est associée à un niveau d'accès (Gratuit, Classique ou Premium).
                Pour consulter l'intégralité d'un document, vous devez être connecté et disposer
                d'un abonnement au moins équivalent à son niveau d'accès.
              </p>
            </div>
            <div className="value-card">
              <i className="fa-solid fa-credit-card"></i>
              <h4>Processus de paiement en 3 étapes</h4>
              <p>
                1. Remplissez vos coordonnées sur le formulaire sécurisé.<br />
                2. Validez et suivez la procédure de paiement via Monetbil.<br />
                3. Une fois le paiement confirmé, votre abonnement est activé immédiatement.
              </p>
            </div>
            <div className="value-card">
              <i className="fa-solid fa-rotate"></i>
              <h4>Changement de formule à tout moment</h4>
              <p>
                Vous pouvez passer d'une formule à une autre à tout moment depuis votre espace
                « Mon compte ». La mise à niveau est immédiate après paiement.
              </p>
            </div>
            <div className="value-card">
              <i className="fa-solid fa-clock"></i>
              <h4>Validité de l'abonnement</h4>
              <p>
                L'abonnement est valable 1 an à partir de la date de paiement. Vous serez notifié
                avant l'expiration pour renouveler votre abonnement.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Subscriptions;